import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { UsageRow } from "@/hooks/useAdminMetrics";

const PALETTE = [
  "hsl(var(--primary))",
  "hsl(190 80% 55%)",
  "hsl(280 70% 65%)",
  "hsl(45 90% 60%)",
  "hsl(140 60% 50%)",
  "hsl(0 75% 60%)",
  "hsl(220 70% 60%)",
  "hsl(330 70% 60%)",
];

interface Row {
  key: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}

export function BreakdownCard({
  title,
  usage,
  groupBy,
  activeKey,
  onSelect,
}: {
  title: string;
  usage: UsageRow[];
  groupBy: "call_type" | "model";
  activeKey?: string | null;
  onSelect?: (key: string | null) => void;
}) {
  const { rows, total } = useMemo(() => {
    const map = new Map<string, Row>();
    for (const r of usage) {
      const key = (r[groupBy] || "unknown") as string;
      const cur =
        map.get(key) ?? { key, calls: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
      cur.calls += 1;
      cur.inputTokens += r.input_tokens || 0;
      cur.outputTokens += r.output_tokens || 0;
      cur.cost += Number(r.estimated_cost_usd ?? 0);
      map.set(key, cur);
    }
    const arr = Array.from(map.values()).sort((a, b) => b.cost - a.cost);
    return { rows: arr, total: arr.reduce((s, r) => s + r.cost, 0) };
  }, [usage, groupBy]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No data.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-[160px,1fr]">
            <div className="h-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    dataKey="cost"
                    nameKey="key"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={1}
                    onClick={(d) =>
                      onSelect?.(activeKey === (d as Row).key ? null : (d as Row).key)
                    }
                  >
                    {rows.map((r, i) => (
                      <Cell
                        key={r.key}
                        fill={PALETTE[i % PALETTE.length]}
                        opacity={!activeKey || activeKey === r.key ? 1 : 0.35}
                        cursor={onSelect ? "pointer" : undefined}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const r = payload[0].payload as Row;
                      const pct = total ? (r.cost / total) * 100 : 0;
                      return (
                        <div className="rounded-md border bg-popover p-2 text-xs shadow-md">
                          <div className="font-medium">{r.key}</div>
                          <div className="text-muted-foreground">
                            ${r.cost.toFixed(4)} · {pct.toFixed(1)}%
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1 text-xs">
              {rows.map((r, i) => {
                const pct = total ? (r.cost / total) * 100 : 0;
                const isActive = activeKey === r.key;
                return (
                  <button
                    type="button"
                    key={r.key}
                    onClick={() => onSelect?.(isActive ? null : r.key)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left transition-colors",
                      isActive ? "bg-muted" : "hover:bg-muted/50",
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-sm"
                        style={{ background: PALETTE[i % PALETTE.length] }}
                      />
                      <span className="truncate">{r.key}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3 tabular-nums text-muted-foreground">
                      <span>{r.calls}</span>
                      <span className="font-medium text-foreground">
                        ${r.cost.toFixed(4)}
                      </span>
                      <span className="w-10 text-right">{pct.toFixed(1)}%</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
